import { env } from '@dx-ui/framework-env';
import set from 'lodash/set';
import { LoginData, LoginErrors } from './login-form.types';
import { ANALYTICS_GLOBAL_CLICK_EVENT, normalizeHonorsTier } from '@dx-ui/config-metrics';
import { sendReward } from '@dx-ui/framework-conductrics';

export const FORGOT_INFO_LINK = '/hilton-honors/login/forgot-information/';
export const SECURE_INFO_LINK = '/p/global-privacy-statement/';
export const JOIN_LINK = '/hilton-honors/join/';
export const OHW_BASE_URL = env('OHW_BASE_URL', 'https://www.test.hilton.com/');
export const B2B_PACKAGE_CODES = [
  'B2B',
  'SMB',
  'SMBP',
  'SMBO',
  'SMBA',
  'SMBC',
  'SMBE',
  'H4LP',
  'H4LO',
  'H4LA',
  'H4LADV',
  'H4LE',
] as const;

export const SIGN_IN_CLICK_ID = {
  INIT: 'signin_init',
  SUCCESS: 'signin_success',
  FORM_VALIDATION: 'formvalidationerror',
  PAGE_STOP: 'pagestoperror',
} as const;

export type SignInClickId = (typeof SIGN_IN_CLICK_ID)[keyof typeof SIGN_IN_CLICK_ID];

type B2BContext = 'personal' | 'business' | 'luxury';
export type SignInProfileStatus = 'personal' | 'business_travel' | 'luxury';

type B2BProgram = 'h4b' | 'h4l';

const getB2BProgram = (packageCode: string): B2BProgram | null => {
  if (!(B2B_PACKAGE_CODES as readonly string[]).includes(packageCode)) {
    return null;
  }

  const packagePrefix = packageCode.slice(0, 3);
  if (packagePrefix === 'H4L') {
    return 'h4l';
  }

  if (packagePrefix === 'B2B' || packagePrefix === 'SMB') {
    return 'h4b';
  }

  return null;
};

const isB2BContext = (value: unknown): value is B2BContext =>
  value === 'personal' || value === 'business' || value === 'luxury';

function getB2BContext(): B2BContext | null {
  try {
    const b2bContextFromLocalStorage = localStorage.getItem('b2bContext');
    if (b2bContextFromLocalStorage) {
      const parsedB2BContext = JSON.parse(b2bContextFromLocalStorage);
      if (isB2BContext(parsedB2BContext)) {
        return parsedB2BContext;
      }
    }
  } catch {
    // fall through to URL param check
  }

  try {
    const params = new URLSearchParams(window.location.search);
    const smbRate = params.get('smbRate');
    const ltaRate = params.get('ltaRate');
    const hasSmbRate = smbRate === 'true' || smbRate === '1';
    const hasLtaRate = ltaRate === 'true' || ltaRate === '1';

    if (hasSmbRate && hasLtaRate) {
      return null;
    }

    if (hasSmbRate) {
      return 'business';
    }

    if (hasLtaRate) {
      return 'luxury';
    }
  } catch {
    // ignore
  }

  return null;
}

export function resolveSignInProfileStatus(
  packageCodes: LoginData['hhonorsPackageCodes']
): SignInProfileStatus | undefined {
  const b2bContext = getB2BContext();
  const hasH4BPackage = packageCodes?.some((pkg) => getB2BProgram(pkg) === 'h4b');
  const hasH4LPackage = packageCodes?.some((pkg) => getB2BProgram(pkg) === 'h4l');

  if (!hasH4BPackage && !hasH4LPackage) {
    return undefined;
  }

  if (b2bContext === 'business' && hasH4BPackage) {
    return 'business_travel';
  }

  if (b2bContext === 'luxury' && hasH4LPackage) {
    return 'luxury';
  }

  return 'personal';
}

export function buildUrl(path: string, language: string, ocode?: string, forwardPageURL?: string) {
  const url = new URL(`${language}${path}`, OHW_BASE_URL);

  if (ocode) {
    url.searchParams.set('ocode', ocode);
  }

  if (forwardPageURL) {
    url.searchParams.set('forwardPageURL', forwardPageURL);
  }

  return url.toString();
}

export function trackLoadLoginForm() {
  if (window?.digitalData && window?._satellite) {
    set(window.digitalData, 'global.page.loginStatus', 'logged-out');
  }
}

export function trackLoginClick(value: SignInClickId | string, loginData?: LoginData) {
  if (window?.digitalData && window?._satellite && value) {
    set(window.digitalData, 'click.clickID', value);

    if (value === SIGN_IN_CLICK_ID.SUCCESS) {
      const profileStatus = resolveSignInProfileStatus(loginData?.hhonorsPackageCodes);
      if (profileStatus) {
        set(window.digitalData, 'honors.user.profileStatus', profileStatus);
      }
      set(window.digitalData, 'global.page.loginStatus', 'logged-in');
      set(window.digitalData, 'global.user.profileID', loginData?.hhonorsNumber);
      set(
        window.digitalData,
        'global.user.rewardsTier',
        normalizeHonorsTier(loginData?.hhonorsTier)
      );
      set(
        window.digitalData,
        'global.user.userType',
        Array.isArray(loginData?.hhonorsPackageCodes)
          ? formatUserPackages(loginData?.hhonorsPackageCodes)
          : ''
      );
    }

    window._satellite?.track?.(ANALYTICS_GLOBAL_CLICK_EVENT);
  }
}

export function trackLoginError(
  type: (typeof LoginErrors)[keyof typeof LoginErrors],
  source: typeof ANALYTICS_GLOBAL_CLICK_EVENT | string,
  alertMessage: string,
  formFields?: string
) {
  if (window?.digitalData && window?._satellite) {
    set(window.digitalData, 'error', {});
    set(window.digitalData, 'error.errorType', 'signin');
    set(window.digitalData, 'error.alertMessage', alertMessage);

    if (type === LoginErrors.INVALID_ENTRY) {
      set(window.digitalData, 'click.clickID', SIGN_IN_CLICK_ID.FORM_VALIDATION);
      set(window.digitalData, 'error.formFields', formFields);
    } else {
      set(window.digitalData, 'click.clickID', SIGN_IN_CLICK_ID.PAGE_STOP);
      set(window.digitalData, 'error.errorCode', type);
    }
    window._satellite?.track?.(source);
  }
}

type UserPackage = Record<'packageName', string>;

const formatUserPackages = (packages: UserPackage[] | string[]): string => {
  return packages.map((pkg) => (typeof pkg === 'string' ? pkg : pkg.packageName)).join(', ');
};

export const isBusinessUser = (packages: LoginData['hhonorsPackageCodes']): boolean =>
  packages?.some((pkg) => (B2B_PACKAGE_CODES as readonly string[]).includes(pkg)) ?? false;

export const passwordRequirementsValidation = {
  uppercase: /[A-Z]/,
  lowercase: /[a-z]/,
  numOrSpecial: /[0-9#?!@$%^&*-]/,
  length: /^.{8,32}$/,
};

const hhonorsNumberValidation = /^\d{8,10}$/;
export const sendUsernameLoginReward = (username: string) => {
  if (!hhonorsNumberValidation.test(username)) {
    sendReward('g-login-with-username');
  }
};
